import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors the mobile welcome series (mobile/src/welcome/tour-context.tsx), adapted
// to the web sidebar. `dot` maps each stage onto the 7-dot progress rail.
export const WEB_STAGES = [
  { id: 'welcome', dot: 0, kind: 'full-welcome' },
  { id: 'profile', dot: 1, kind: 'card-profile' },
  {
    id: 'nav-rail', dot: 2, kind: 'spotlight', target: '[data-testid="pro-rail"]', nav: '/dashboard', info: true,
    title: 'Your hubs live here',
    body: 'Planning, Intelligence, Learning and Results — every tool in Homegrown is one click away down this sidebar.',
  },
  {
    id: 'nav-main', dot: 2, kind: 'spotlight', target: '[data-testid="nav-main-action"]', nav: '/dashboard', info: true,
    title: 'Your main action',
    body: 'Your most-used action sits right up top — jump to it in one click, or hover it for the full quick-action wheel.',
  },
  {
    id: 'today-weather', dot: 3, kind: 'spotlight', target: '[data-tour="today-weather"]', nav: '/dashboard', info: true,
    title: 'Your daily garden forecast',
    body: 'Soil warmth, frost risk, UV and your next harvest — personalised to your exact location.',
  },
  {
    id: 'today-advice', dot: 3, kind: 'spotlight', target: '[data-tour="today-advice"]', nav: '/dashboard', prompt: 'Click the card to explore',
    title: 'Today’s advice',
    body: 'This tells you exactly what to do in your garden today — one clear action, every morning.',
  },
  {
    id: 'planner', dot: 4, kind: 'spotlight', target: '[data-testid="what-can-i-grow-pill"]', nav: '/planner', prompt: 'Give it a click', clickTarget: true,
    title: 'What can I grow?',
    body: 'See everything you can sow, plant and harvest this month in your area.',
  },
  {
    id: 'planner-floating', dot: 4, kind: 'floating',
    title: 'Plan your whole season',
    body: 'Tick crops as Planned or Planted and track your entire growing season, right here.',
  },
  {
    id: 'exchange', dot: 5, kind: 'exchange', nav: '/dashboard', autoMs: 4200,
    title: 'The Vege Exchange',
    body: 'Find fresh produce and seeds from home gardeners near you — swap your surplus and meet your neighbours.',
  },
  { id: 'ready', dot: 6, kind: 'full-ready' },
];

const TourContext = createContext(null);
const START_PATHS = ['/dashboard', '/today'];

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [stageIndex, setStageIndex] = useState(-1);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSpot, setHelpSpot] = useState(null);
  const startedRef = useRef(false);

  // Fetch the current user's welcome flags; retry on navigation until we have them
  // (the user may log in after the provider first mounts).
  useEffect(() => {
    if (user) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => {});
  }, [location.pathname, user]);

  const go = useCallback((i) => {
    const st = WEB_STAGES[i];
    if (!st) return;
    setStageIndex(i);
    axios.patch(`${API}/users/me`, { welcome_stage: i }, { withCredentials: true }).catch(() => {});
    if (st.nav && location.pathname !== st.nav) navigate(st.nav);
  }, [navigate, location.pathname]);

  const next = useCallback(() => {
    setStageIndex((cur) => {
      const ni = cur + 1;
      if (ni >= WEB_STAGES.length) {
        axios.patch(`${API}/users/me`, { welcome_completed: true, welcome_stage: 0 }, { withCredentials: true }).catch(() => {});
        navigate('/dashboard');
        return -1;
      }
      const st = WEB_STAGES[ni];
      axios.patch(`${API}/users/me`, { welcome_stage: ni }, { withCredentials: true }).catch(() => {});
      if (st.nav && location.pathname !== st.nav) navigate(st.nav);
      return ni;
    });
  }, [navigate, location.pathname]);

  const complete = useCallback(() => {
    setStageIndex(-1);
    axios.patch(`${API}/users/me`, { welcome_completed: true, welcome_stage: 0 }, { withCredentials: true }).catch(() => {});
    setUser((u) => (u ? { ...u, welcome_completed: true } : u));
    navigate('/dashboard');
  }, [navigate]);

  const start = useCallback((replay = false) => {
    startedRef.current = true;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => go(0));
  }, [go]);

  const refreshUser = useCallback(() => {
    return axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => {});
  }, []);

  // ── Help Centre ──
  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const clearHelpSpot = useCallback(() => setHelpSpot(null), []);
  const showMe = useCallback((guide) => {
    setHelpOpen(false);
    if (guide.route && location.pathname !== guide.route) navigate(guide.route);
    setTimeout(() => setHelpSpot(guide), guide.route && location.pathname !== guide.route ? 450 : 60);
  }, [navigate, location.pathname]);

  // Auto-start once for an onboarded grower with a region who hasn't seen it.
  useEffect(() => {
    if (stageIndex !== -1 || startedRef.current || !user) return;
    if (!user.onboarded || !user.region || user.welcome_completed) return;
    if (!START_PATHS.includes(location.pathname)) return;
    startedRef.current = true;
    const resume = Math.min(Math.max(user.welcome_stage || 0, 0), WEB_STAGES.length - 1);
    go(resume);
  }, [user, location.pathname, stageIndex, go]);

  const value = {
    active: stageIndex >= 0,
    stage: WEB_STAGES[stageIndex] || null,
    stageIndex,
    user,
    start,
    next,
    complete,
    refreshUser,
    helpOpen,
    helpSpot,
    openHelp,
    closeHelp,
    showMe,
    clearHelpSpot,
  };
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  return useContext(TourContext) || { active: false, stage: null, start: () => {}, next: () => {}, complete: () => {}, openHelp: () => {}, closeHelp: () => {}, showMe: () => {}, clearHelpSpot: () => {}, helpOpen: false, helpSpot: null };
}
